import { AxiosResponse } from 'axios'
import { acceptHMRUpdate, defineStore } from 'pinia'

import api from '@/lib/api'
import { transformApiFormErrors, transformOrderTemplate } from '@/lib/utils/transformEntries'

import { useSessionStore } from './session'

import { ORDER_TEMPLATE_MODES } from '@/settings/constants'
import { OrderTemplate, ApiOrderTemplatesPayload, ApiOrderTemplate, ApiError } from '@/types'

type State = {
  modes: typeof ORDER_TEMPLATE_MODES
  activeMode: keyof typeof ORDER_TEMPLATE_MODES
  orderTemplateFormData: typeof defaultOrderTemplateData
}
type Actions = {
  getCompanyOrderTemplates: (params: {
    company: string
    department: string
  }) => Promise<OrderTemplate[]>
  setupOrderTemplateForm: (params: {
    mode: keyof typeof ORDER_TEMPLATE_MODES
    orderTemplateData?: OrderTemplate
  }) => void
  sendOrderTemplate: (
    orderTemplateData: ApiOrderTemplatesPayload,
  ) => Promise<AxiosResponse<ApiOrderTemplate>>
  validateOrderTemplate: (thresholdData: ApiOrderTemplatesPayload) => Promise<{ ok: boolean }>
  resetState: () => void
}

const defaultOrderTemplateData: OrderTemplate = {
  id: 0,
  active: true,
  b2bUnit: {
    code: '',
    name: '',
  },
  billingB2bUnit: {
    code: '',
    name: '',
  },
  shippingB2bUnit: {
    code: '',
    name: '',
  },
  department: {
    businessUnit: '',
    id: 0,
    name: '',
  },
  billTo: '',
  billToAddressString: '',
  billingContact: '',
  billingEmails: [],
  currency: '',
  contact: '',
  contactName: '',
  contactEmail: '',
  name: '',
  expirationDate: '',
  notes: '',
  poNumber: '',
  shipTo: '',
  shipToAddressString: '',
  shippingContact: '',
  shippingEmails: [],
  poAllowedAmount: 0,
  remainingAmount: 0,
}

export const useOrderTemplateStore = defineStore<string, State, Record<string, never>, Actions>(
  'order-template',
  {
    state: () => ({
      modes: ORDER_TEMPLATE_MODES,
      activeMode: 'NEW',
      orderTemplateFormData: { ...defaultOrderTemplateData },
    }),

    actions: {
      async getCompanyOrderTemplates({ company = '', department = '' }) {
        const response = await api.inventoryTracking.getOrderTemplates({
          pageSize: 9999,
          company,
          department,
        })
        const { orderTemplates } = response.data
        return orderTemplates.map(transformOrderTemplate)
      },

      setupOrderTemplateForm({ mode, orderTemplateData }) {
        const sessionStore = useSessionStore()
        const userCompany = {
          code: sessionStore.user?.unit?.uid,
          name: sessionStore.user?.unit?.name,
        }
        const newOrderTemplateData = {
          ...defaultOrderTemplateData,
          b2bUnit: userCompany,
          billingB2bUnit: userCompany,
          shippingB2bUnit: userCompany,
        }

        const formData =
          orderTemplateData && (mode === this.modes.EDIT || mode === this.modes.COPY)
            ? orderTemplateData
            : newOrderTemplateData
        this.orderTemplateFormData = formData
        this.activeMode = mode
      },

      async sendOrderTemplate(orderTemplateData) {
        const handlers = {
          [this.modes.NEW]: api.inventoryTracking.addNewOrderTemplate,
          [this.modes.COPY]: api.inventoryTracking.addNewOrderTemplate,
          [this.modes.EDIT]: api.inventoryTracking.updateOrderTemplate,
        }

        try {
          return handlers[this.activeMode](orderTemplateData)
        } catch (error) {
          const transformedError = error as unknown as ApiError
          transformedError.response.data = transformApiFormErrors(transformedError)
          throw transformedError
        }
      },

      async validateOrderTemplate(thresholdData) {
        try {
          await api.inventoryTracking.validateOrderTemplate(thresholdData)
          return { ok: true }
        } catch (error) {
          return {
            ok: false,
            errors: transformApiFormErrors(error),
          }
        }
      },

      resetState() {
        this.$reset()
      },
    },
  },
)

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useOrderTemplateStore, import.meta.hot))
}
